import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalPath?: string;
  ogType?: string;
  schemaJson?: Record<string, unknown>;
}

export default function SEO({ title, description, keywords, canonicalPath, ogType = 'website', schemaJson }: SEOProps) {
  const location = useLocation();

  useEffect(() => {
    const currentPath = canonicalPath || location.pathname;
    const baseUrl = 'https://hanmanhua.com';
    const fullUrl = `${baseUrl}${currentPath}`;

    document.title = title;

    const setMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);

    const canonicalEl = document.querySelector('link[rel="canonical"]');
    if (canonicalEl) canonicalEl.setAttribute('href', fullUrl);

    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:locale', 'zh_CN', true);

    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    // Schema.org JSON-LD
    if (schemaJson) {
      let scriptEl = document.querySelector('#schema-jsonld');
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.setAttribute('id', 'schema-jsonld');
        scriptEl.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(schemaJson);
    }

    return () => {
      const scriptEl = document.querySelector('#schema-jsonld');
      if (scriptEl) scriptEl.remove();
    };
  }, [title, description, keywords, canonicalPath, ogType, schemaJson, location.pathname]);

  return null;
}